import React, { useEffect, useState } from "react"
import { useParams, useNavigate, Link } from "react-router-dom"
import { useBallparkRoom } from "./useBallparkRoom"
import { getIdentity } from "./ballparkRoom"
import { getQuestions } from "../../../data/ballparkQuestions"
import { Screen, BpButton, PlayerDot, C, F, useBallparkFonts } from "./BallparkUI"
import LobbyScreen from "./LobbyScreen"
import GuessScreen from "./GuessScreen"
import BetScreen from "./BetScreen"
import RevealScreen from "./RevealScreen"
import GameOverScreen from "./GameOverScreen"
import { playSound } from "../../../utils/ballparkSounds"

const BallparkGame = () => {
  useBallparkFonts()
  const { code } = useParams()
  const navigate = useNavigate()
  const { room, loading, error } = useBallparkRoom(code)
  const { playerId } = getIdentity()
  const [showScores, setShowScores] = useState(false)

  // Follow the host to a rematch room
  useEffect(() => {
    if (room?.nextRoom && room.phase === "gameover") {
      navigate(`/ballpark/${room.nextRoom}`)
    }
  }, [room?.nextRoom, room?.phase, navigate])

  if (loading) {
    return (
      <Screen style={{ justifyContent: "center", alignItems: "center" }}>
        <div style={{ fontWeight: 800, color: C.muted }}>Loading…</div>
      </Screen>
    )
  }

  if (error || !room) {
    return (
      <Screen style={{ justifyContent: "center", alignItems: "center", gap: 16 }}>
        <div style={{ fontFamily: F.display, fontWeight: 800, fontSize: 24 }}>
          Game not found
        </div>
        <div style={{ color: C.muted, fontWeight: 700, textAlign: "center" }}>
          That room may have expired. Codes last about 12 hours.
        </div>
        <Link to="/ballpark" style={{ width: "100%", textDecoration: "none" }}>
          <BpButton>Back to Ballpark</BpButton>
        </Link>
      </Screen>
    )
  }

  // A visitor with a link who never joined: bounce to landing to enter a name
  if (!room.players?.[playerId]) {
    return (
      <Screen style={{ justifyContent: "center", alignItems: "center", gap: 16 }}>
        <div style={{ fontFamily: F.display, fontWeight: 800, fontSize: 24 }}>
          You're not in this game yet
        </div>
        <Link to="/ballpark" style={{ width: "100%", textDecoration: "none" }}>
          <BpButton>Join with code {code}</BpButton>
        </Link>
      </Screen>
    )
  }

  const isHost = room.hostId === playerId
  const question =
    room.phase === "guessing" || room.phase === "betting"
      ? getQuestions(room.packIds)[room.questionOrder[room.questionIndex]]
      : null
  const props = { room, code, playerId, isHost, question, setShowScores }

  return (
    <div style={{ position: "relative", overflow: "hidden", width: "100%" }}>
      {/* Standings Drawer */}
      <div
        style={{
          position: "absolute",
          top: showScores ? 0 : "-100%",
          left: 0,
          right: 0,
          background: C.bg,
          borderBottom: `3.5px solid ${C.gold}`,
          boxShadow: "0 10px 30px rgba(59, 46, 34, 0.2)",
          zIndex: 100,
          transition: "top 0.3s cubic-bezier(0.16, 1, 0.3, 1)",
          padding: "24px 20px 20px",
          borderBottomLeftRadius: 24,
          borderBottomRightRadius: 24,
          maxWidth: 520,
          margin: "0 auto",
          width: "100%",
          boxSizing: "border-box",
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <div style={{ fontFamily: F.display, fontWeight: 800, fontSize: 20, color: C.ink }}>
            🏆 Current Standings
          </div>
          <button
            onClick={() => {
              playSound("click")
              setShowScores(false)
            }}
            style={{
              background: "none",
              border: "none",
              fontFamily: F.display,
              fontWeight: 800,
              fontSize: 26,
              color: C.rust,
              cursor: "pointer",
              padding: 4,
              lineHeight: 1,
            }}
          >
            ×
          </button>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {Object.entries(room.players)
            .sort((a, b) => b[1].score - a[1].score)
            .map(([pid, p], i) => (
              <div
                key={pid}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  background: C.card,
                  border: `1.5px solid ${C.border}`,
                  borderRadius: 14,
                  padding: "10px 12px",
                }}
              >
                <span style={{ fontWeight: 800, fontSize: 14, color: C.muted, width: 16 }}>
                  {i + 1}
                </span>
                <PlayerDot color={p.color} size={24} />
                <span style={{ fontWeight: 800, fontSize: 15, color: C.ink }}>
                  {p.name} {pid === playerId && " (You)"}
                </span>
                <span style={{ marginLeft: "auto", fontFamily: F.display, fontWeight: 800, fontSize: 18, color: C.rust }}>
                  {p.score} pts
                </span>
              </div>
            ))}
        </div>
      </div>

      {/* Screen Render */}
      {(() => {
        switch (room.phase) {
          case "lobby":
            return <LobbyScreen {...props} />
          case "guessing":
            return <GuessScreen {...props} />
          case "betting":
            return <BetScreen {...props} />
          case "reveal":
            return <RevealScreen {...props} />
          case "gameover":
            return <GameOverScreen {...props} />
          default:
            return null
        }
      })()}
    </div>
  )
}

export default BallparkGame
